import React, { FC } from 'react';
import clsx from 'clsx';
import { format, isValid, parse, parseISO } from 'date-fns';
import { IDoctorNotifications, IHandleNotification, INotifications } from 'utils/models';
import { groupBy } from 'utils/helpers';

import Button from 'components/Button';
import LoaderSpinner from 'components/LoaderSpinner';

interface INotifData extends IDoctorNotifications {
  loading?: boolean;
  handleReadNotification: ({ ...props }: IHandleNotification) => Promise<any> | void;
}

const parseCreatedAt = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isValid(v) ? v : null;

  if (typeof v === 'string') {
    const iso = parseISO(v);
    if (isValid(iso)) return iso;

    const withMsOffset = parse(v, 'yyyy-MM-dd HH:mm:ss.SSS xx', new Date());
    if (isValid(withMsOffset)) return withMsOffset;

    const withOffset = parse(v, 'yyyy-MM-dd HH:mm:ss xx', new Date());
    if (isValid(withOffset)) return withOffset;
  }

  const d = new Date(v);
  return isValid(d) ? d : null;
};

const NotificationMessages: FC<INotifData> = ({
  notifications,
  loading,
  handleReadNotification
}) => {
  const list = notifications || [];

  const grouped = groupBy(list, (n: INotifications) => {
    const d = parseCreatedAt(n.created_at);
    return d ? format(d, 'yyyy-MM-dd') : '__unknown__';
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center my-2">
        <LoaderSpinner className="w-5 h-5" />
      </div>
    );
  }

  const dayKeys = Object.keys(grouped);

  return (
    <div className="hidden md:block max-h-[calc(100vh-12em)] overflow-y-auto">
      {dayKeys.length > 0 &&
        dayKeys.map((key) => {
          const header =
            key !== '__unknown__'
              ? format(parse(key, 'yyyy-MM-dd', new Date()), 'eeee d MMMM yyyy')
              : 'Unknown date';

          return (
            <div key={key} className="mt-4">
              <p className="text-gray-900 mt-8 text-sm text-opacity-30 font-bold">{header}</p>

              {grouped[key].map((item: INotifications, idx: number) => {
                const dt = parseCreatedAt(item.created_at);
                const timeStr = dt ? format(dt, "h:mmaaaaa'm'") : '';

                const notifId =
                  (item as any).id ??
                  (item as any).notification_id ??
                  (item as any).notificationId ??
                  (item as any)._id ??
                  null;

                const safeKey = notifId ?? `no-id-${key}-${idx}`;
                const disabled = !notifId;

                return (
                  <Button
                    key={safeKey}
                    variant="invisible"
                    className="text-left"
                    disabled={disabled}
                    onClick={() => {
                      if (!notifId) {
                        console.warn('Notification missing id:', item);
                        return;
                      }
                      handleReadNotification({
                        id: String(notifId),
                        is_read: !!item.is_read,
                        link: item.link
                      });
                    }}
                  >
                    <div className="-ml-5 grid grid-flow-col gap-3 items-center pt-2.5 px-4 border-t border-hazel-light-green-700">
                      <div
                        className={clsx(
                          'w-2.5 h-2.5 rounded-full -ml-4 mr-4',
                          item.is_read ? 'bg-transparent' : 'bg-hazel-orange-100'
                        )}
                      />
                      <div className="flex justify-between w-full">
                        <div className="w-full">
                          <h3 className="text-sm font-extrabold">{item.title}</h3>
                          <p className="font-regular text-xs leading-5 pr-5 break-normal">
                            {item.content}
                          </p>
                        </div>
                        <p className="text-gray-900 text-opacity-30 font-regular text-xs w-16">
                          {timeStr}
                        </p>
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          );
        })}
    </div>
  );
};

export default NotificationMessages;
